import React, { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next';
import { useDeleteGrocery, useGetAllGrocery } from '../hooks/hooks';
import "react-loading-skeleton/dist/skeleton.css";
import AddGroceriesModal from './../modal/AddGroceries'
import { useNavigate } from 'react-router-dom';
import { DeleteIcon, EditIcon, LowStockIcon, NDGrocerieI, NDVegetableI, NoStockIcon, PayStatusIcon, PlusIcon, TableIcon } from '../assets/icons/icon';
import { ButtonWithIcon } from '../components/Common/Buttons';
import EntityListPage from '../components/Common/EntityListPage';

function GroceriesCopy() {
  const { t } = useTranslation();
  const [stockFilter, setStockFilter] = useState('all'); // 'all', 'low', 'out'
  const navigate = useNavigate();

  const groceryData = useGetAllGrocery().data || [];

  const lowStockCount = useMemo(
    () => groceryData.filter((i) => i.isStockLow).length,
    [groceryData]
  );

  const outOfStockCount = useMemo(
    () =>
      groceryData.filter(
        (i) => !i.stockQuantity || i.stockQuantity === 0
      ).length,
    [groceryData]
  );

  const getUnitTypes = (unitType) => {
    switch (unitType.toLowerCase()) {
      case 'gram':
        return 'ગ્રામ (Gram)';
      case 'kgs':
        return 'કિગ્રા (KGS)';
      case 'liter':
        return 'લિટર (Liter)';
      case 'ml':
        return 'મિલીલીટર (ML)';
      case 'pieces':
        return 'નંગ (Pieces)';
      case 'per_person':
        return 'માણસો દીઠ (Per Person)';
      case 'box':
        return 'બોક્સ (Box)';
      default:
        return unitType;
    }
  }

  const GroceryCard = ({ item, index, onEdit, onDelete }) => (
    <div className="bg-white rounded-xl shadow-card border border-gray-100 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 p-6">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              {item.name}
            </h3>
            {item.isStockLow && (
              <LowStockIcon className='w-5 h-5 text-red-500' />
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <NDGrocerieI className='w-4 h-4 text-gray-500' />
              <span className="text-sm text-gray-600">
                Stock: {item.stockQuantity && item.stockUnit ? (item.stockQuantity + " " + getUnitTypes(item.stockUnit)) : "Not set"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <NDVegetableI className='w-4 h-4 text-gray-500' />
              <span className="text-sm text-gray-600">
                Threshold: {item.stockThreshold && item.stockThresholdUnit ? (item.stockThreshold + " " + getUnitTypes(item.stockThresholdUnit)) : "Not set"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <PayStatusIcon className='w-4 h-4 text-gray-500' />
              <span className="text-sm text-gray-600">{getUnitTypes(item.unitType)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <ButtonWithIcon title="Edit" variant='secondary' Icon={PlusIcon} iconClass={`mr-0 w-4 h-4 ${item.stockThreshold && item.stockThresholdUnit
            ? 'text-blue-600'
            : 'text-gray-400'
            }`} btnclass={`p-[5px] rounded-md transition-all duration-200 ${item.stockThreshold && item.stockThresholdUnit
              ? 'hover:bg-blue-50 border border-blue-300'
              : 'opacity-50 cursor-not-allowed border border-gray-300'
              }`} onClick={(e) => {
                if (!item.stockThreshold || !item.stockThresholdUnit) return;
                e.stopPropagation();
                navigate('/groceries/add-update-stock', { state: { isEdit: true, isEditItem: item, openStockPanel: true } });
              }} />
          <ButtonWithIcon title="Edit" variant='secondary' Icon={EditIcon} iconClass="text-green-600 mr-0" btnclass="p-[5px] hover:bg-green-50 rounded-md transition-all border border-green-300" onClick={onEdit} />
          <ButtonWithIcon title="Edit" variant='secondary' Icon={DeleteIcon} iconClass="w-4 h-4 mr-0 text-red-500" btnclass="p-[5px] text-red-600 hover:bg-red-50 rounded-md border border-red-300" onClick={onDelete} />
        </div>
      </div>
    </div>
  );

  const TableRow = ({ item, index, startIndex, onEdit, onDelete }) => (
    <div
      className={`w-full flex py-4 font-medium text-sm text-gray-700 border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200
          ${index % 2 === 0 ? "bg-gray-50/50" : "bg-white"}`}
    >
      <div className="w-[5%] pl-6 flex items-center">
        {item.isStockLow && (
          <LowStockIcon className='w-5 h-5 text-red-500' />
        )}
      </div>
      <div className="w-[5%] flex items-center font-medium text-gray-500">{startIndex + index + 1}</div>
      <div className="w-[25%] flex items-center pr-4">
        <div className="font-semibold text-gray-900 truncate" title={item.name}>{item.name}</div>
      </div>
      <div className="w-[20%] flex items-center pr-4">
        <div className="text-gray-700 truncate" title={item.stockQuantity && item.stockUnit ? (item.stockQuantity + " " + getUnitTypes(item.stockUnit)) : ""}>
          {item.stockQuantity && item.stockUnit ? (item.stockQuantity + " " + getUnitTypes(item.stockUnit)) : "Not set"}
        </div>
      </div>
      <div className="w-[15%] flex items-center pr-4">
        <div className="text-gray-700 truncate" title={item.stockThreshold && item.stockThresholdUnit ? (item.stockThreshold + " " + getUnitTypes(item.stockThresholdUnit)) : ""}>
          {item.stockThreshold && item.stockThresholdUnit ? (item.stockThreshold + " " + getUnitTypes(item.stockThresholdUnit)) : "Not set"}
        </div>
      </div>
      <div className="w-[15%] flex items-center pr-4">
        <div className="text-gray-700 truncate" title={getUnitTypes(item.unitType)}>{getUnitTypes(item.unitType)}</div>
      </div>
      <div className="w-[15%] flex items-center">
        <div className="flex items-center gap-2">
          <ButtonWithIcon title="Edit" variant='secondary' Icon={PlusIcon} iconClass={`mr-0 w-4 h-4 ${item.stockThreshold && item.stockThresholdUnit
            ? 'text-blue-600'
            : 'text-gray-400'
            }`} btnclass={`p-2 rounded-md transition-all duration-200 ${item.stockThreshold && item.stockThresholdUnit
              ? 'hover:bg-blue-50 border border-blue-300'
              : 'opacity-50 cursor-not-allowed border border-gray-300'
              }`} onClick={(e) => {
                if (!item.stockThreshold || !item.stockThresholdUnit) return;
                e.stopPropagation();
                navigate('/groceries/add-update-stock', { state: { isEdit: true, isEditItem: item, openStockPanel: true } });
              }} />
          <ButtonWithIcon title="Edit" variant='secondary' Icon={EditIcon} iconClass="text-green-600 mr-0" btnclass="p-2 hover:bg-green-50 rounded-md transition-all border border-green-300" onClick={onEdit} />
          <ButtonWithIcon title="Delete" variant='secondary' Icon={DeleteIcon} iconClass="w-4 h-4 mr-0 text-red-500" btnclass="p-2 text-red-600 hover:bg-red-50 rounded-md border border-red-300" onClick={onDelete} />
        </div>
      </div>
    </div>
  );

  const FilterStock = ({ setStockFilter, lowStockCount, outOfStockCount }) => (
    <div className="flex items-center gap-2">
      <button
        onClick={() => setStockFilter('all')}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${stockFilter === 'all'
          ? 'bg-blue-100 text-blue-700 border border-blue-300'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
      >
        <TableIcon />
        All
      </button>
      <button
        onClick={() => setStockFilter('low')}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${stockFilter === 'low'
          ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
      >
        <LowStockIcon />
        Low Stock ({lowStockCount})
      </button>
      <button
        onClick={() => setStockFilter('out')}
        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${stockFilter === 'out'
          ? 'bg-red-100 text-red-700 border border-red-300'
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
      >
        <NoStockIcon />
        Out of Stock ({outOfStockCount})
      </button>
    </div>
  )

  const getNoDataDesc = useCallback((searchText) => {
    return searchText ? 'Try adjusting your search terms' : 'Get started by adding your first grocery item';
  }, []);


  const filterFn = useCallback(
    (list, searchText, stockFilter) => {
      let result = list;

      if (stockFilter === "low") {
        result = result.filter(i => i.isStockLow === true);
      }

      if (stockFilter === "out") {
        result = result.filter(
          i => !i.stockQuantity || i.stockQuantity === 0
        );
      }

      if (searchText.trim()) {
        const v = searchText.toLowerCase();
        result = result.filter(i =>
          i.name?.toLowerCase().includes(v)
        );
      }

      return result;
    },
    []
  );
  return (
    <>

      <EntityListPage
        title={t('Groceries')}
        subtitle={t('manage_your_grocery_inventory_and_stock_levels')}
        addButtonText={t('add_new_groceries')}
        searchPlaceholder={t('search_Grocery_lbl')}
        searchChildren={<FilterStock setStockFilter={setStockFilter} lowStockCount={lowStockCount} outOfStockCount={outOfStockCount} />}

        fetchHook={useGetAllGrocery}
        deleteHook={useDeleteGrocery}
        filterFn={filterFn}
        extraFilters={stockFilter}
        tableColumns={[
          { key: "", label: "", className: "w-[5%]", width: 30 },
          { key: "#", label: "#", className: "w-[5%]", width: 50 },
          { key: "name", label: t('groceries_name'), className: "w-[25%]", width: 200 },
          { key: "Currentstock", label: t('Currentstock'), className: "w-[20%]", width: 150 },
          { key: "stockThreshold", label: t('stockThreshold'), className: "w-[15%]", width: 120 },
          { key: "unit", label: t('unitType'), className: "w-[15%]", width: 100 },
          { key: "action", label: t('Action'), className: "w-[15%]", width: 80 },
        ]}

        TableRow={TableRow}
        Card={GroceryCard}
        AddEditModal={AddGroceriesModal}

        getNoDataDesc={getNoDataDesc}
        emptyIcon={<NDGrocerieI />}
        emptyText="No groceries found"
        buttonText={'Add Grocery'}
        deleteMsg={t('delete_grocery_confirmation_msg')}
      />

      {/* <StockModal
        isOpen={isStockPopupOpen}
        onClose={handleClosePopup}
        isEdit={isEdit}
        isEditItem={isEditItem}
      /> */}

    </>
  )
}

export default GroceriesCopy